import { v4 as uuidV4 } from 'uuid';
import type { HubOptionScore, HubScoreValueType } from 'dorian-shared/types/branch/HubOption';
import {
  registerHubOptionDeserializer,
  registerHubOptionSerializer,
} from './hubSerialize';

// Self-registering module: imported for side effects from hubOptionTransform.ts.
// Score is per-option, stored as JSON column on HubOptions. Backend is a passive
// pass-through (hub_option.js:297) — the wire format is defined entirely by the
// spec contract: { numerator: { type, value }, denominator: { type, value } }.

// API contract — what backend stores in the score JSON column.
type ApiScoreSide = {
  type: HubScoreValueType;
  // literal: number; variable: BookVariables(id)
  value: number | null;
};
type ApiScore = {
  numerator: ApiScoreSide;
  denominator: ApiScoreSide;
};

function isScoreEmpty(score: HubOptionScore): boolean {
  const numEmpty = score.numeratorType === 'literal'
    ? score.numeratorLiteral == null || score.numeratorLiteral === ''
    : score.numeratorVariableId == null;
  const denEmpty = score.denominatorType === 'literal'
    ? score.denominatorLiteral == null || score.denominatorLiteral === ''
    : score.denominatorVariableId == null;
  return numEmpty && denEmpty;
}

function nestedSideToFlat(
  side: Partial<ApiScoreSide> | null | undefined,
  prefix: 'numerator' | 'denominator',
): Pick<HubOptionScore, 'numeratorType' | 'numeratorLiteral' | 'numeratorVariableId'>
  | Pick<HubOptionScore, 'denominatorType' | 'denominatorLiteral' | 'denominatorVariableId'> {
  const type: HubScoreValueType = side?.type === 'variable' ? 'variable' : 'literal';
  const value = side?.value ?? null;
  if (prefix === 'numerator') {
    return {
      numeratorType: type,
      numeratorLiteral: type === 'literal' && value != null ? String(value) : null,
      numeratorVariableId: type === 'variable' && typeof value === 'number' ? value : null,
    };
  }
  return {
    denominatorType: type,
    denominatorLiteral: type === 'literal' && value != null ? String(value) : null,
    denominatorVariableId: type === 'variable' && typeof value === 'number' ? value : null,
  };
}

function flatSideToNested(
  type: HubScoreValueType,
  literal: string | null,
  variableId: number | null,
): ApiScoreSide {
  if (type === 'variable') {
    return { type: 'variable', value: variableId };
  }
  // Literal: store as number per spec ("value: literal number"). Empty → null.
  if (literal == null || literal === '') return { type: 'literal', value: null };
  const num = Number(literal);
  return { type: 'literal', value: Number.isFinite(num) ? num : null };
}

registerHubOptionDeserializer((option) => {
  if (!option.score) return;
  const incoming = option.score as unknown as Partial<ApiScore>;
  const numFlat = nestedSideToFlat(incoming.numerator, 'numerator') as Pick<
    HubOptionScore, 'numeratorType' | 'numeratorLiteral' | 'numeratorVariableId'
  >;
  const denFlat = nestedSideToFlat(incoming.denominator, 'denominator') as Pick<
    HubOptionScore, 'denominatorType' | 'denominatorLiteral' | 'denominatorVariableId'
  >;
  // eslint-disable-next-line no-param-reassign -- match in-place mutation style of hubOptionTransform.ts
  option.score = {
    uuid: uuidV4(),
    numeratorUuid: uuidV4(),
    denominatorUuid: uuidV4(),
    ...numFlat,
    ...denFlat,
  };
});

registerHubOptionSerializer((option) => {
  if (!option.score) return;
  if (isScoreEmpty(option.score)) {
    // eslint-disable-next-line no-param-reassign
    option.score = null;
    return;
  }
  const apiScore: ApiScore = {
    numerator: flatSideToNested(
      option.score.numeratorType,
      option.score.numeratorLiteral,
      option.score.numeratorVariableId,
    ),
    denominator: flatSideToNested(
      option.score.denominatorType,
      option.score.denominatorLiteral,
      option.score.denominatorVariableId,
    ),
  };
  // eslint-disable-next-line no-param-reassign
  option.score = apiScore as unknown as HubOptionScore;
});
